import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../../api';

const FooterSection = ({
  websiteData,
  handleInputChange,
  handleSave,
  tenantId
}) => {
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState({ visible: false, type: '', message: '' });
  const [localFooterData, setLocalFooterData] = useState({
    copyrightText: '',
    showBackToTop: true,
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: ''
    },
    contactInfo: {
      email: '',
      phone: '',
      address: '',
      mapEmbedUrl: ''
    },
    quickLinks: []
  });
  const [lastSavedData, setLastSavedData] = useState(null);

  // Sync local state when websiteData changes (important for displaying saved data)
  useEffect(() => {
    if (websiteData?.footer) {
      console.log('📥 [FooterSection] Received footer data from parent:', websiteData.footer);
      const footerData = websiteData.footer;
      setLocalFooterData({
        copyrightText: footerData.copyrightText || '',
        showBackToTop: footerData.showBackToTop !== undefined ? footerData.showBackToTop : true,
        socialMedia: footerData.socialMedia || {
          facebook: '',
          twitter: '',
          instagram: '',
          linkedin: '',
          youtube: ''
        },
        contactInfo: footerData.contactInfo || {
          email: '',
          phone: '',
          address: '',
          mapEmbedUrl: ''
        },
        quickLinks: footerData.quickLinks || []
      });
      setLastSavedData(JSON.stringify(footerData));
    }
  }, [websiteData]);

  // Notification timeout
  useEffect(() => {
    if (!notify.visible) return;
    const timer = setTimeout(() => setNotify({ visible: false, type: '', message: '' }), 3000);
    return () => clearTimeout(timer);
  }, [notify.visible]);

  // Handle footer save - Manual only
  const handleSaveFooter = async () => {
    console.log('💾 [FooterSection] Manual save triggered');
    console.log('📦 [FooterSection] Current tenantId:', tenantId);
    console.log('📦 [FooterSection] Current localFooterData:', localFooterData);
    
    // Prevent multiple saves
    if (saving) {
      console.log('⏳ [FooterSection] Save already in progress, skipping...');
      return;
    }
    
    // Check if there are actual changes
    const currentStr = JSON.stringify(localFooterData);
    if (lastSavedData === currentStr) {
      console.log('✅ [FooterSection] No changes detected, skipping save');
      setNotify({ visible: true, type: 'info', message: 'No changes to save' });
      return;
    }
    
    setSaving(true);
    const payload = localFooterData;

    try {
      console.log('🚀 [FooterSection] Sending PUT request to:', `/api/website/${tenantId}/footer`);
      console.log('📤 [FooterSection] Request payload:', payload);
      
      const resp = await api.put(`/api/website/${tenantId}/footer`, payload);
      
      console.log('📥 [FooterSection] Response received:', resp);
      console.log('📥 [FooterSection] Response data:', resp?.data);
      
      if (!resp?.data?.success) {
        const msg = resp?.data?.message || 'Failed to save footer';
        console.error('❌ [FooterSection] Save failed:', msg);
        setNotify({ visible: true, type: 'error', message: msg });
        throw new Error(msg);
      }
      
      console.log('✅ [FooterSection] Save successful!');
      setNotify({ visible: true, type: 'success', message: 'Footer saved successfully' });
      
      // Update last saved with the new data
      setLastSavedData(currentStr);
      console.log('✅ [FooterSection] Last saved updated:', currentStr);
      
      // Call parent handleSave if provided
      if (handleSave) {
        console.log('📞 [FooterSection] Calling parent handleSave');
        await handleSave('footer', payload);
      }
      
    } catch (error) {
      console.error('❌ [FooterSection] Error saving footer:', error);
      if (!notify.visible) {
        const errorMsg = error?.response?.data?.message || error?.message || 'Failed to save footer';
        setNotify({
          visible: true,
          type: 'error',
          message: errorMsg
        });
      }
    } finally {
      setSaving(false);
      console.log('🔓 [FooterSection] Save completed, saving set to false');
    }
  };

  // Update local state without auto-saving
  const updateLocalData = (section, field, value) => {
    console.log('✏️ [FooterSection] updateLocalData called:', { section, field, value });
    
    setLocalFooterData(prev => {
      let newData;
      if (field) {
        newData = {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      } else {
        newData = {
          ...prev,
          [section]: value
        };
      }
      console.log('📝 [FooterSection] New data:', newData);
      return newData;
    });
    
    // Show unsaved notification
    const currentStr = JSON.stringify(localFooterData);
    if (lastSavedData && lastSavedData !== currentStr) {
      setNotify({ visible: true, type: 'info', message: 'Changes pending - click Save to apply' });
    }
  };

  // Update social media link
  const updateSocialMedia = (platform, value) => {
    console.log('📱 [FooterSection] updateSocialMedia:', { platform, value });
    updateLocalData('socialMedia', platform, value);
  };

  // Update contact info
  const updateContactInfo = (field, value) => {
    console.log('📞 [FooterSection] updateContactInfo:', { field, value });
    updateLocalData('contactInfo', field, value);
  };

  // Add quick link
  const addQuickLink = () => {
    console.log('➕ [FooterSection] addQuickLink called');
    const newLinks = [
      ...localFooterData.quickLinks,
      { text: 'New Link', url: '#' }
    ];
    console.log('📝 [FooterSection] New quick links:', newLinks);
    updateLocalData('quickLinks', null, newLinks);
  };

  // Remove quick link
  const removeQuickLink = (index) => {
    console.log('🗑️ [FooterSection] removeQuickLink called:', index);
    const newLinks = localFooterData.quickLinks.filter((_, i) => i !== index);
    console.log('📝 [FooterSection] Updated quick links:', newLinks);
    updateLocalData('quickLinks', null, newLinks);
  };

  // Update quick link
  const updateQuickLink = (index, field, value) => {
    console.log('✏️ [FooterSection] updateQuickLink:', { index, field, value });
    const newLinks = localFooterData.quickLinks.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    );
    console.log('📝 [FooterSection] Updated quick links:', newLinks);
    updateLocalData('quickLinks', null, newLinks);
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const currentStr = JSON.stringify(localFooterData);
    return lastSavedData !== currentStr;
  };

  return (
    <div className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      {/* Settings Forms Column */}
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
          {/* Header Action Bar */}
          <div className="px-5 py-4 border-b border-slate-150 bg-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900">Footer Configuration</h3>
              <p className="text-xs text-slate-500 mt-0.5">Configure your portal website baseline footer data references.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {notify.visible && (
                <span className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
                  notify.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                    : notify.type === 'info'
                    ? 'bg-blue-50 text-blue-800 border-blue-200'
                    : 'bg-rose-50 text-rose-800 border-rose-200'
                }`}>
                  {notify.message}
                </span>
              )}

              <button
                onClick={handleSaveFooter}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg shadow-xs text-white transition-all ${
                  saving 
                    ? 'bg-slate-300 cursor-not-allowed' 
                    : hasUnsavedChanges()
                      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-1 focus:ring-blue-500'
                      : 'bg-slate-500 hover:bg-slate-600 focus:ring-2 focus:ring-offset-1 focus:ring-slate-400'
                }`}
              >
                <FiSave className="h-3.5 w-3.5" />
                {saving ? 'Saving...' : hasUnsavedChanges() ? 'Save Changes *' : 'Saved'}
              </button>
            </div>
          </div>

          {/* Change Warnings Banner */}
          {hasUnsavedChanges() && (
            <div className="px-5 py-2.5 bg-amber-50/80 border-b border-amber-100 flex items-center">
              <p className="text-xs font-medium text-amber-700">
                ⚠️ You have unsaved changes. Remember to click "Save Changes" button above to preserve records.
              </p>
            </div>
          )}

          <div className="p-5 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card - Contact Information */}
              <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-4 space-y-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Contact Information</h4>
                
                <div className="space-y-3">
                  <div>
                    <label htmlFor="footer-email" className="block text-xs font-semibold text-slate-600 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="footer-email"
                      value={localFooterData.contactInfo?.email || ''}
                      onChange={(e) => updateContactInfo('email', e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="contact@school.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="footer-phone" className="block text-xs font-semibold text-slate-600 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="footer-phone"
                      value={localFooterData.contactInfo?.phone || ''}
                      onChange={(e) => updateContactInfo('phone', e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="footer-address" className="block text-xs font-semibold text-slate-600 mb-1">
                      Physical Address
                    </label>
                    <textarea
                      id="footer-address"
                      value={localFooterData.contactInfo?.address || ''}
                      onChange={(e) => updateContactInfo('address', e.target.value)}
                      rows={3}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                      placeholder="123 Main Street, City, State"
                    />
                  </div>

                  <div>
                    <label htmlFor="map-embed" className="block text-xs font-semibold text-slate-600 mb-1">
                      Google Maps Embed URL
                    </label>
                    <input
                      type="url"
                      id="map-embed"
                      value={localFooterData.contactInfo?.mapEmbedUrl || ''}
                      onChange={(e) => updateContactInfo('mapEmbedUrl', e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="https://maps.google.com/embed?pb=..."
                    />
                    <p className="mt-1 text-[10px] text-slate-400">
                      Paste the dynamic frame embed string extracted from maps layout share maps tags.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card - Social Media Links */}
              <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-4 space-y-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Social Media Handles</h4>
                
                <div className="space-y-3">
                  {Object.entries(localFooterData.socialMedia || {}).map(([platform, url]) => (
                    <div key={platform}>
                      <label 
                        htmlFor={`social-${platform}`} 
                        className="block text-xs font-semibold text-slate-600 mb-1 capitalize"
                      >
                        {platform} URL Link
                      </label>
                      <input
                        type="url"
                        id={`social-${platform}`}
                        value={url || ''}
                        onChange={(e) => updateSocialMedia(platform, e.target.value)}
                        className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder={`https://${platform}.com/account`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Grid block - Quick Links array mapping */}
              <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-200 flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Footer Quick Links Mappings</h4>
                  <button
                    onClick={addQuickLink}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-xs"
                  >
                    <FiPlus className="h-3.5 w-3.5" />
                    <span>Add Link</span>
                  </button>
                </div>
                
                <div className="p-4 space-y-3">
                  {(localFooterData.quickLinks || []).map((link, index) => (
                    <div key={index} className="flex items-end gap-3 p-3 bg-slate-50/40 rounded-xl border border-slate-150 transition-all">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                            Link Label Text
                          </label>
                          <input
                            type="text"
                            value={link.text || ''}
                            onChange={(e) => updateQuickLink(index, 'text', e.target.value)}
                            className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-hidden focus:ring-1 focus:ring-blue-500 transition-all"
                            placeholder="About Us, Contact, etc."
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                            Target Redirect path / URL
                          </label>
                          <input
                            type="text"
                            value={link.url || ''}
                            onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                            className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md bg-white focus:outline-hidden focus:ring-1 focus:ring-blue-500 transition-all"
                            placeholder="/about, /contact, etc."
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeQuickLink(index)}
                        className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all"
                        title="Remove link item"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  {(localFooterData.quickLinks || []).length === 0 && (
                    <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <p className="text-xs text-slate-500">No custom quick navigation redirect links built inside this profile yet.</p>
                      <button
                        onClick={addQuickLink}
                        className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                      >
                        <FiPlus className="h-3.5 w-3.5" />
                        <span>Build first reference path</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Options layout rows */}
              <div className="md:col-span-2 border-t border-slate-200 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Extended Footnote Elements</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="copyright-text" className="block text-xs font-semibold text-slate-600 mb-1">
                      Legal Copyright Text String
                    </label>
                    <input
                      type="text"
                      id="copyright-text"
                      value={localFooterData.copyrightText || ''}
                      onChange={(e) => updateLocalData('copyrightText', null, e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="© 2026 ERP School. All rights reserved."
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div>
                      <label htmlFor="back-to-top" className="block text-xs font-bold text-slate-800">
                        Back to Top Scroll Anchor
                      </label>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Render a fast visibility scroll shortcut badge helper.
                      </p>
                    </div>
                    
                    <label className="inline-flex items-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        id="back-to-top"
                        checked={localFooterData.showBackToTop || false}
                        onChange={(e) => updateLocalData('showBackToTop', null, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="relative w-10 h-5 bg-slate-200 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live System Preview Frame Column */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-4 lg:sticky lg:top-24">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Footer View Preview</h3>
            <p className="text-xs text-slate-400 mt-0.5">Real-time graphic layout mock viewport tracker.</p>
          </div>

          <div className="bg-slate-900 text-slate-100 p-5 rounded-xl min-h-[380px] flex flex-col justify-between border border-slate-850">
            <div className="space-y-5">
              {/* Info Frame Section */}
              {(localFooterData.contactInfo?.email || localFooterData.contactInfo?.phone || localFooterData.contactInfo?.address) && (
                <div>
                  <h5 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 border-b border-slate-800 pb-1">Contact Office</h5>
                  <div className="space-y-1.5 text-xs text-slate-300">
                    {localFooterData.contactInfo?.email && (
                      <p className="truncate flex items-center gap-1.5">
                        <span className="text-slate-500 text-[10px]">✉</span> {localFooterData.contactInfo.email}
                      </p>
                    )}
                    {localFooterData.contactInfo?.phone && (
                      <p className="truncate flex items-center gap-1.5">
                        <span className="text-slate-500 text-[10px]">📞</span> {localFooterData.contactInfo.phone}
                      </p>
                    )}
                    {localFooterData.contactInfo?.address && (
                      <p className="text-xs text-slate-300 leading-relaxed flex items-start gap-1.5">
                        <span className="text-slate-500 text-[10px] mt-0.5">📍</span>
                        <span className="break-words max-w-full block">{localFooterData.contactInfo.address}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Links block preview arrays */}
              {(localFooterData.quickLinks || []).length > 0 && (
                <div>
                  <h5 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 border-b border-slate-800 pb-1">Quick Navigation</h5>
                  <div className="grid grid-cols-2 gap-1.5">
                    {localFooterData.quickLinks.map((link, index) => (
                      <span
                        key={index}
                        className="text-slate-300 text-xs truncate hover:text-white transition-colors cursor-pointer"
                      >
                        {link.text || `Link ${index + 1}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Channels Preview badges */}
              {Object.values(localFooterData.socialMedia || {}).some(url => url) && (
                <div>
                  <h5 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 border-b border-slate-800 pb-1">Follow Channels</h5>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(localFooterData.socialMedia || {}).map(([platform, url]) => 
                      url && (
                        <span 
                          key={platform} 
                          className="bg-slate-800 hover:bg-slate-750 border border-slate-700/60 text-slate-200 px-2 py-1 rounded-md text-[10px] uppercase font-semibold tracking-wider"
                        >
                          {platform}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Copyright line display */}
            <div className="border-t border-slate-800 pt-3 mt-4 flex items-center justify-between gap-2">
              <p className="text-[10px] text-slate-500 leading-normal max-w-[70%]">
                {localFooterData.copyrightText || '© 2026 School ERP Dashboard profile map system.'}
              </p>
              
              {localFooterData.showBackToTop && (
                <button type="button" disabled className="bg-slate-800 text-slate-400 px-2 py-1 rounded-md text-[9px] uppercase font-bold tracking-tight opacity-80 shrink-0">
                  ↑ Top
                </button>
              )}
            </div>
          </div>
          
          <p className="text-[10px] text-slate-400 leading-relaxed text-center italic bg-slate-50 py-2 border border-slate-150 rounded-lg">
            Changes inside this configuration card layout must be finalized manually by tapping the Save button above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FooterSection;